// =============================================================================
// src/utils/accessGuard.js
//
// One place in the browser that knows "what can this user do right now".
//   setAccessSnapshot({ role, policies })   AuthContext keeps it fresh
//   can('billing.cancel_invoice')           true / false
//   rule('billing', 'maxDiscountLimit')     a rule value
//   installFetchGuard()                     called once at start-up
//
// The fetch guard: if a screen asks the Smiify API for something the user has
// no access to, the request is NOT sent. The screen gets a normal 403 JSON
// answer, so its own error handling runs and the console stays clean.
// The server checks everything again anyway; this only keeps the browser quiet.
// =============================================================================
import { ACCESS_RULES, SKIP_PREFIXES, findAccessRule } from '../config/accessRules';

const API_MARK = '/api/v1/smiify/sm';

let snapshot = { role: null, policies: null };
const listeners = new Set();

export const setAccessSnapshot = (next) => {
    snapshot = { role: next?.role || null, policies: next?.policies || null };
    listeners.forEach((fn) => { try { fn(snapshot); } catch (e) { /* ignore */ } });
};
export const getAccessSnapshot = () => snapshot;
export const onAccessChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/** can('module.action') with the same rules as hasPermission() */
export const can = (key) => {
    if (!key) return true;
    if (snapshot.role === 'SUPERADMIN') return true;
    const [module, action = 'view'] = String(key).split('.');
    const actions = snapshot.policies?.[module]?.actions;
    if (!actions) return false;
    if (action !== 'view' && actions.view === false) return false;              // master gate
    if (action === 'view' && actions.view === undefined) return Object.values(actions).some((x) => x === true);
    return actions[action] === true;
};

export const canAny = (keys = []) => (Array.isArray(keys) ? keys : [keys]).some((k) => can(k));
export const canAll = (keys = []) => (Array.isArray(keys) ? keys : [keys]).every((k) => can(k));

export const rule = (module, key, fallback) => {
    if (snapshot.role === 'SUPERADMIN') return fallback;
    const v = snapshot.policies?.[module]?.constraints?.[key];
    return v === undefined ? fallback : v;
};

/* ----------------------------- fetch guard ----------------------------- */
const pathOf = (url) => {
    try {
        const u = new URL(url, window.location.origin);
        const i = u.pathname.indexOf(API_MARK);
        return i >= 0 ? (u.pathname.slice(i + API_MARK.length) || '/') : null;
    } catch (e) {
        return null;
    }
};

const blockedResponse = (message) => new Response(
    JSON.stringify({ success: false, code: 'NO_ACCESS', blockedInBrowser: true, message }),
    { status: 403, statusText: 'Forbidden', headers: { 'Content-Type': 'application/json' } },
);

/** Is this request allowed? Returns { allowed, need } */
export const checkRequest = (method, url) => {
    const path = pathOf(url);
    if (path === null) return { allowed: true };                               // not a Smiify API call
    if (SKIP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return { allowed: true };
    if (!snapshot.policies || snapshot.role === 'SUPERADMIN') return { allowed: true }; // not loaded yet: let the server decide
    const found = findAccessRule(method, path);
    if (!found || found.need === null || found.need === 'SERVER') return { allowed: true };
    const need = Array.isArray(found.need) ? found.need : [found.need];
    return { allowed: canAny(need), need };
};

// Called when the SERVER says "no access": the browser's copy is out of date, so reload it
let onServerDenied = null;
export const setOnServerDenied = (fn) => { onServerDenied = typeof fn === 'function' ? fn : null; };

let installed = false;
export const installFetchGuard = () => {
    if (installed || typeof window === 'undefined' || !window.fetch) return;
    installed = true;
    const original = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
        try {
            const url = typeof input === 'string' ? input : (input?.url || String(input));
            const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
            const result = checkRequest(method, url);
            if (!result.allowed) return Promise.resolve(blockedResponse('You do not have access to this.'));
            if (pathOf(url) !== null) {
                return original(input, init).then((res) => {
                    if (res.status === 403 && onServerDenied) {
                        res.clone().json().then((j) => {
                            if (j && (j.code === 'NO_ACCESS' || j.code === 'WRONG_BRANCH')) onServerDenied();
                        }).catch(() => {});
                    }
                    return res;
                });
            }
        } catch (e) { /* never break a request because of the guard */ }
        return original(input, init);
    };
};

export const _rulesForTests = ACCESS_RULES;